import { roomResponseSchema, type RoomResponse, type RoomWorkstation } from '@growup/arena-shared';
import { Button, cx, Input, Notice } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { formatMinutes, formatTime, minutesUntil } from '../format';
import { LimitBadge, LimitSummary } from './limits';
import { WaitlistPanel } from './WaitlistPanel';

const REFRESH_MS = 10_000;

function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Room view (RF-D2): every PC with what is happening on it, refreshed every 10 s. */
export function RoomView({ arenaId }: { arenaId: string }) {
  const [room, setRoom] = useState<RoomResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson(`/api/arena/arenas/${arenaId}/room`, roomResponseSchema)
      .then((r) => {
        setRoom(r);
        setError(null);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [arenaId]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  if (!room) return error ? <Notice tone="danger">{error}</Notice> : null;

  const busy = room.workstations.filter((w) => w.session).length;
  const flagged = room.workstations.filter(
    (w) => w.session?.limits && ['over_continuous', 'over_daily'].includes(w.session.limits.state),
  ).length;
  const positioned = room.workstations.some((w) => w.posX > 0 || w.posY > 0);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="flex flex-col gap-4">
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <p className="text-body text-gu-ink-muted">
          {busy} de {room.workstations.length} postos ocupados
          {flagged > 0 ? (
            <span className="ml-2 font-medium text-gu-danger">
              <span aria-hidden="true">✕ </span>
              {flagged} acima do limite
            </span>
          ) : null}
          {room.waitlist.length > 0 ? (
            <span className="ml-2 text-gu-warning">
              · {room.waitlist.length} em espera: os limites aplicam-se
            </span>
          ) : (
            <span className="ml-2 text-gu-ink-muted">· sem lista de espera</span>
          )}
        </p>
        {room.workstations.length === 0 ? (
          <p className="text-gu-ink-muted">
            Esta arena ainda não tem postos. Um administrador cria-os em Configuração.
          </p>
        ) : positioned ? (
          <RoomMap workstations={room.workstations} onChanged={load} />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {room.workstations.map((w) => (
              <WorkstationCard key={w.id} workstation={w} onChanged={load} />
            ))}
          </div>
        )}
      </div>
      <WaitlistPanel arenaId={arenaId} waitlist={room.waitlist} onChanged={load} />
    </div>
  );
}

/**
 * PCs laid out as they are in the room. Each PC's X/Y (set in Configuração)
 * picks its column and row, so cards line up like the desks without ever
 * overlapping. A plain list on small screens.
 */
function RoomMap({
  workstations,
  onChanged,
}: {
  workstations: RoomWorkstation[];
  onChanged: () => void;
}) {
  const xs = [...new Set(workstations.map((w) => w.posX))].sort((a, b) => a - b);
  const ys = [...new Set(workstations.map((w) => w.posY))].sort((a, b) => a - b);
  const ordered = [...workstations].sort((a, b) => a.posY - b.posY || a.posX - b.posX);
  return (
    <>
      <div
        className="hidden gap-3 xl:grid"
        style={{ gridTemplateColumns: `repeat(${String(xs.length)}, minmax(0, 1fr))` }}
      >
        {workstations.map((w) => (
          <div
            key={w.id}
            style={{ gridColumn: xs.indexOf(w.posX) + 1, gridRow: ys.indexOf(w.posY) + 1 }}
          >
            <WorkstationCard workstation={w} onChanged={onChanged} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:hidden">
        {ordered.map((w) => (
          <WorkstationCard key={w.id} workstation={w} onChanged={onChanged} />
        ))}
      </div>
    </>
  );
}

export function WorkstationCard({
  workstation: w,
  onChanged,
}: {
  workstation: RoomWorkstation;
  onChanged: () => void;
}) {
  const now = useNow();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [note, setNote] = useState('');

  const act = async (method: string, path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await send(method, path, body);
      setClosing(false);
      setNote('');
      onChanged();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const s = w.session;
  const command = w.pendingCommand;
  const border = !w.online
    ? 'border-dashed border-gu-line'
    : s?.limits && ['over_continuous', 'over_daily'].includes(s.limits.state)
      ? 'border-gu-danger'
      : s
        ? 'border-gu-ink'
        : 'border-gu-line';

  return (
    <article
      className={cx('flex flex-col gap-2 border-2 bg-gu-raised p-3', border)}
      aria-label={`Posto ${w.name}`}
    >
      <header className="flex items-center justify-between">
        <h3 className="font-display text-title uppercase">{w.name}</h3>
        {w.online ? (
          <span className="text-caption text-gu-success">
            <span aria-hidden="true">● </span>Ligado
          </span>
        ) : (
          <span className="text-caption text-gu-ink-muted">
            <span aria-hidden="true">✕ </span>
            {w.lastSeenAt ? `Sem ligação desde ${formatTime(w.lastSeenAt)}` : 'Nunca ligou'}
          </span>
        )}
      </header>

      {s ? (
        <div className="flex flex-col gap-1">
          <p className="text-body font-semibold">{s.isEvent ? 'Sessão de evento' : s.login}</p>
          <p className="text-caption text-gu-ink-muted">
            Desde {formatTime(s.startedAt)} ·{' '}
            {formatMinutes((now - new Date(s.startedAt).getTime()) / 60_000)}
          </p>
          {s.limits ? (
            <>
              <LimitBadge status={s.limits} />
              <LimitSummary status={s.limits} />
            </>
          ) : null}
          {s.duringBreak ? (
            <p className="text-caption text-gu-info">
              <span aria-hidden="true">⏸ </span>Entrou durante a pausa obrigatória
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-body text-gu-ink-muted">
          <span aria-hidden="true">○ </span>Livre
        </p>
      )}

      {command ? (
        <p className="border border-gu-warning/60 bg-gu-warning/10 px-2 py-1 text-caption text-gu-warning">
          {command.kind === 'lock'
            ? minutesUntil(command.executeAt, now) > 0
              ? `Bloqueia às ${formatTime(command.executeAt)} (faltam ${formatMinutes(Math.ceil(minutesUntil(command.executeAt, now)))})`
              : 'Bloqueio enviado, à espera do posto'
            : 'Desbloqueio de evento enviado'}
        </p>
      ) : null}

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {closing && s ? (
        <div className="flex flex-col gap-2">
          <Input
            placeholder="Motivo (obrigatório)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="danger"
              disabled={busy || note.trim().length < 3}
              onClick={() =>
                void act('POST', `/api/arena/sessions/${s.id}/close`, { note: note.trim() })
              }
            >
              Fechar agora
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setClosing(false)}>
              Voltar
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {command ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => void act('DELETE', `/api/arena/commands/${command.id}`)}
            >
              Cancelar
            </Button>
          ) : (
            <>
              {s ? (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={busy}
                  onClick={() => void act('POST', `/api/arena/workstations/${w.id}/lock`, {})}
                >
                  {s.isEvent ? 'Bloquear' : 'Bloquear (aviso 5 min)'}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => void act('POST', `/api/arena/workstations/${w.id}/unlock-event`)}
                >
                  Desbloquear para evento
                </Button>
              )}
              {s && !s.isEvent ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => setClosing(true)}
                >
                  Fechar sessão…
                </Button>
              ) : null}
            </>
          )}
        </div>
      )}
    </article>
  );
}
